/* global __webpack_public_path__ */
import { render } from 'preact'
import qs from 'qs'
import 'preact/debug' // condition in this file prevents it from usage outside of dev env
import tracker from '../components/tracker/publish'
import detectLogout from '../utils/detectLogout'
import initializeDelightedSurvey from '../utils/initializeDelightedSurvey'
import globals, { ModulesContext, publish } from '../utils/globals'
import { addTranslationDict } from './translationsUtils/translations'
import loadScript from 'load-script'
import '../../../sass/src/default-style.scss'
import { isHtmlElement } from './typeGuards'

import axios from 'axios'
import { csrfToken } from '../preact-components/utils/csrfToken'
import { TranslationContext } from './translationsUtils/context'
import { CurrentUserContext, UserPermissionContext } from '../server-middleware/userContext'
import { CurrentPageContext, page404WithoutContext as page404 } from '../pages/utils'
import { jsxLoader } from './jsxLoader'

declare let __webpack_public_path__: string
declare let require: (module: string) => any
declare let module: {
  hot?: {
    accept: (cb: () => void) => void
  }
}

declare global {
  interface Window {
    inSided: any
    _babelPolyfill: {}
    hljs: {
      highlightBlock(el: Element): void
    }
  }
}

const logError = (error: unknown): void => {
  /** TODO:
   * It is a temporal logging solution, needs to be reused
   */
  console.log(error)
  try {
    window.Rollbar.error(error)
  } catch {}
}

axios.defaults.headers.common = {
  'X-Requested-With': 'XMLHttpRequest',
  'X-CSRF-TOKEN': csrfToken()
}

if (typeof window !== 'undefined' && typeof window.CustomEvent !== 'function') require('../utils/customEventPolyfill')
if (typeof window !== 'undefined' && !window._babelPolyfill) require('@babel/polyfill')
if (typeof NodeList !== 'undefined' && typeof NodeList.prototype.forEach !== 'function') {
  require('../utils/forEachPolyfill').default()
}

detectLogout()

const scriptElement = document.getElementById('insided-preact-app')
if (scriptElement) {
  globals('region', scriptElement.getAttribute('data-region') || '')
  globals('environment', scriptElement.getAttribute('data-environment') || '')
  globals('communityId', scriptElement.getAttribute('data-communityId') || '')
  globals('cssLink', scriptElement.getAttribute('data-cssLink') || '')
  globals('cssVariables', document.getElementById('css-variables')?.innerHTML || '')
  globals('communityUrl', scriptElement.getAttribute('data-communityUrl') || '')
  globals('controlUrl', scriptElement.getAttribute('data-controlUrl') || '')
  globals('application', scriptElement.getAttribute('data-application') || '')
  globals('communityLanguage', document.getElementsByTagName('html')[0].getAttribute('lang') || '')
  globals('defaultCdn', scriptElement.getAttribute('data-defaultCdn') || '')
  globals('facebookAppId', scriptElement.getAttribute('data-fb-app-id') || '')
  globals('eventLogger', scriptElement.getAttribute('data-eventLogger') || '')
  globals('communityLanguage', scriptElement.getAttribute('data-language') || '')

  const basePath = scriptElement.getAttribute('data-basepath' || '')

  if (basePath) {
    globals('basePath', basePath.replace(/\/js\/$/, '/'))
    __webpack_public_path__ = basePath.replace(/\/js\/$/, '/') // eslint-disable-line
  } else {
    globals('basePath', '')
  }
  let activeModules = scriptElement.getAttribute('data-modules') || ''
  try {
    globals('activeModules', JSON.parse(activeModules) || [])
  } catch (e) {
    globals('activeModules', [])
  }
}

if (globals('application') === 'control') {
  initializeDelightedSurvey()

  if (window.sessionStorage.getItem('UserLoggedInControl')) {
    tracker.trackEvent('userevent', {
      type: '[Control] User Visited',
      user_id: window.inSidedData.user.userid,
      user_email: window.inSidedData.user.email,
      user_name: window.inSidedData.user.name,
      user_primary_role: window.inSidedData.user.mainRole,
      user_custom_roles: window.inSidedData.user.role
    })
    window.sessionStorage.removeItem('UserLoggedInControl')
  }
}

interface ComponentParams {
  phrases?: {}
  translations?: {}
  parentEl?: HTMLElement
}

async function initialiseComponent(
  componentName: string,
  el: HTMLElement,
  params: ComponentParams,
  unrender = false
): Promise<Element | void> {
  let child = el.firstChild as Element // preact render() also accepts text Nodes as third argument
  if (!el.firstChild) {
    child = document.createElement('div')
    el.appendChild(child)
  }

  if (unrender) {
    render('', el, child)
    return
  }
  const LoadedComponent = await jsxLoader(componentName)

  addTranslationDict(params.phrases || params.translations)
  params.parentEl = el
  if (!window.inSidedData) {
    logError(`window.inSidedData is not ready during initialization of "${componentName}"`)
    return render(<span data-widget={componentName} />, el, child)
  }
  const user = window.inSidedData.user
  if (!LoadedComponent) return

  let pageContext = page404
  try {
    pageContext = JSON.parse(scriptElement?.getAttribute('data-pageContext') || '{}')
  } catch (err) {}

  let userPermissions = { permissions: [], readableCategories: [] }
  try {
    userPermissions = JSON.parse(scriptElement?.getAttribute('data-userPermissions') || '')
  } catch (err) {}

  render(
    <ModulesContext.Provider value={globals('activeModules') || []}>
      <TranslationContext.Provider
        value={{
          getUsedTranslations: () => ({}),
          translations: params.phrases || params.translations || null,
          registerPhrase: () => {},
          language: globals('communityLanguage')
        }}
      >
        <CurrentUserContext.Provider
          value={{
            id: user.userid ? parseInt(user.userid) : null,
            email: user.email ?? '',
            rank: user.rank,
            rankName: user.rankName,
            rankIcon: user.rankIcon,
            name: user.name,
            role: user.role,
            mainRole: user.mainRole,
            loginSource: user.loginSource,
            registerSource: user.registerSource,
            avatar: user.avatar ?? '',
            isModerator: user.isModerator,
            pmUnreadCount: user.pmUnreadCount,
            pmTotalCount: user.pmTotalCount,
            topicsCount: user.topicsCount,
            repliesCount: user.repliesCount,
            solvedCount: user.solvedCount
          }}
        >
          <CurrentPageContext.Provider value={{ ...pageContext, fullUrl: new URL(window.location.href) }}>
            <UserPermissionContext.Provider value={userPermissions}>
              <LoadedComponent {...params} />
            </UserPermissionContext.Provider>
          </CurrentPageContext.Provider>
        </CurrentUserContext.Provider>
      </TranslationContext.Provider>
    </ModulesContext.Provider>,
    el,
    child
  )

  if (module.hot) {
    module.hot.accept(async () => {
      const LoadedComponent = await jsxLoader(componentName)
      render(<LoadedComponent {...params} />, el, child)
    })
  }

  return
}

function highlightCodeBlocks(wrapperComponent: HTMLElement | Document) {
  const codeComponentsToHighlight = wrapperComponent.querySelectorAll('pre code')
  if (codeComponentsToHighlight.length) {
    const link = document.createElement('link')
    link.href = __webpack_public_path__ + 'ckeditor/plugins/codesnippet/lib/highlight/styles/github.css'
    link.rel = 'stylesheet'
    document.head.appendChild(link)

    loadScript(__webpack_public_path__ + 'ckeditor/plugins/codesnippet/lib/highlight/highlight.pack.js', () => {
      codeComponentsToHighlight.forEach((block) => {
        window.hljs.highlightBlock(block)
      })
    })
  }
}

function prepareSpoilersAndQuotes(wrapperComponent: Element | Document) {
  wrapperComponent.querySelectorAll('content-spoiler, content-quote').forEach((content: Element) => {
    if (!content.getAttribute('data-prepared')) {
      const el = document.createElement('div')
      const childrenWrapper = document.createElement('div')
      childrenWrapper.className = content.tagName.toLowerCase()
      while (content.firstChild) {
        const el = content.removeChild(content.firstChild)
        childrenWrapper.appendChild(el)
      }
      content.appendChild(el)
      content.appendChild(childrenWrapper)

      content.setAttribute('data-prepared', 'true')
    }
  })
}

export function instantiatePreactComponents(wrapperComponent: HTMLElement | Document) {
  const components = wrapperComponent.querySelectorAll(
    '[data-preact], user-mention, content-quote, content-spoiler, oembed'
  )

  for (const el of components) {
    if (!isHtmlElement(el)) continue
    try {
      window.insided.initialiseComponent(
        el.dataset.preact || el.tagName.toLowerCase() + '/index',
        el,
        el.dataset.props ? JSON.parse(el.dataset.props) : { ...el.dataset, content: el.innerHTML }
      )
    } catch (error) {
      logError(error)
    }
  }
}

function addGlobalPublishers() {
  document.querySelectorAll('[data-trigger]').forEach((element) => {
    if (!isHtmlElement(element)) return
    element.addEventListener('click', (e) => {
      e.preventDefault()
      if (element.dataset.trigger === 'quote') {
        if (!element.dataset.username) return
        publish('quote', {
          username: element.dataset.username,
          content: element.dataset.content || ''
        })
      }
    })
  })
}

/*
  Dynamically requires components from HTML elements
  Usage example:
  <div
    data-preact="content-helpfulness/ContentHelpfulnessManager"
    data-props="{{ configuration | json_encode }}">
  </div>
 */

const bindPreactComponents = (wrapperComponent: HTMLElement | Document) => {
  prepareSpoilersAndQuotes(wrapperComponent)

  wrapperComponent.querySelectorAll('oembed').forEach((content: Element) => {
    if (!isHtmlElement(content)) return
    content.dataset.url = content.getAttribute('url') || ''
  })

  instantiatePreactComponents(wrapperComponent)

  highlightCodeBlocks(wrapperComponent)
}

window.insided = {
  initialiseComponent,
  bindPreactComponents,
  trackEvent: tracker.trackEvent,
  publish: publish
}

window.insided.bindPreactComponents(document)

addGlobalPublishers()

const searchQuery = window.location.search
const queryParameters = qs.parse(searchQuery, { ignoreQueryPrefix: true })
if (queryParameters.postid) window.location.hash = '#post' + queryParameters.postid

const showcase = document.querySelector('#insided-dev-showcase')
if (showcase) {
  import('./showcase').then(async ({ default: Showcase }) => {
    if (!window.location.search) {
      render(<Showcase />, showcase, showcase.firstElementChild || undefined)
      return
    }
    await import('./showcases')
  })
}
